// src/modules/pedidos/pages/HistorialCargas.js
//
// Historial unificado: pedidos ENTREGADOS a clientes (retira) + pedidos
// CARGADOS al camión / agencia / gira. Cada fila muestra el método de entrega
// y, para camión/gira, los datos del viaje (destino, fecha, nombre).
// Accesible para Admin, Ventas y Encargado.

import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { norm } from "utils/text";

// ── Helpers ───────────────────────────────────────────────
function tsToMs(ts) {
  if (!ts) return 0;
  if (ts.seconds) return ts.seconds * 1000;
  const d = new Date(ts);
  return isNaN(d) ? 0 : d.getTime();
}

function fmtHora(ms) {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

function fmtDiaLargo(ms) {
  if (!ms) return "Sin fecha";
  return new Date(ms).toLocaleDateString("es-UY", { weekday: "long", day: "numeric", month: "long" });
}

function fmtFechaCorta(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("es-UY", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

const METODO_STYLE = {
  RETIRA:  { bg: "#e1f5ee", color: "#0f6e56", label: "🏪 Retira" },
  AGENCIA: { bg: "#e6f1fb", color: "#185fa5", label: "🚌 Agencia" },
  CAMION:  { bg: "#fef3c7", color: "#92400e", label: "🚛 Camión" },
  GIRA:    { bg: "#f5f3ff", color: "#7c3aed", label: "🚚 Gira" },
};

const METODOS_FILTRO = [
  { id: "",        label: "Todos" },
  { id: "RETIRA",  label: "🏪 Retira" },
  { id: "AGENCIA", label: "🚌 Agencia" },
  { id: "CAMION",  label: "🚛 Camión" },
  { id: "GIRA",    label: "🚚 Gira" },
];

export default function HistorialCargas() {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const rol = (profile?.rol || "").toLowerCase();
  const esEncargado = rol === "encargado";
  // Encargado ve solo su depósito; admin y ventas ven todos.
  const depFiltro = esEncargado ? (profile?.deposito || null) : null;

  const [entregados, setEntregados] = useState([]);
  const [cargados, setCargados]     = useState([]);
  const [camiones, setCamiones]     = useState({}); // { camionId: {...} }
  const [loading, setLoading]       = useState(true);

  const [q, setQ]               = useState("");
  const [metodo, setMetodo]     = useState("");
  const [depSel, setDepSel]     = useState(""); // filtro de depósito (admin/ventas)

  // ── Entregados (retira y cualquier entregado a cliente) ──
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "pedidos_entregados"), snap => {
      setEntregados(snap.docs.map(d => ({ id: d.id, ...d.data(), _tipo: "ENTREGADO" })));
      setLoading(false);
    }, err => console.error("[HistorialCargas] entregados", err));
    return () => unsub();
  }, []);

  // ── Cargados al camión/agencia/gira (cargaEstado CARGADO) ──
  useEffect(() => {
    const qy = query(collection(db, "pedidos_despachados"), where("cargaEstado", "==", "CARGADO"));
    const unsub = onSnapshot(qy, snap => {
      setCargados(snap.docs.map(d => ({ id: d.id, ...d.data(), _tipo: "CARGADO" })));
    }, err => console.error("[HistorialCargas] cargados", err));
    return () => unsub();
  }, []);

  // ── Camiones (para resolver destino/fecha/nombre del viaje) ──
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "camiones"), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setCamiones(map);
    }, err => console.error("[HistorialCargas] camiones", err));
    return () => unsub();
  }, []);

  // ── Merge + dedupe + filtros ──
  const filas = useMemo(() => {
    const porId = new Map();
    // Entregados tienen prioridad (estado más final)
    for (const p of entregados) {
      porId.set(p.id, { ...p, _fechaMs: tsToMs(p.entregadoAt || p.despachadoAt) });
    }
    for (const p of cargados) {
      if (porId.has(p.id)) continue;
      porId.set(p.id, { ...p, _fechaMs: tsToMs(p.cargadoAt || p.despachadoAt) });
    }
    let out = [...porId.values()];

    // Depósito: encargado fijo a su depósito; admin/ventas según chip
    out = out.filter(p => {
      if (depFiltro && p.deposito !== depFiltro) return false;
      if (!depFiltro && depSel && p.deposito !== depSel) return false;
      return true;
    });

    if (metodo) out = out.filter(p => (p.metodoEntrega || "") === metodo);

    if (q.trim()) {
      const nq = norm(q.trim());
      out = out.filter(p => {
        const num = norm(String(p.numero || p.id || ""));
        const cli = norm(p.cliente || "");
        const cam = norm(camiones[p.camionId]?.descripcion || p.agencia || "");
        return num.includes(nq) || cli.includes(nq) || cam.includes(nq);
      });
    }

    out.sort((a, b) => b._fechaMs - a._fechaMs);
    return out;
  }, [entregados, cargados, camiones, depFiltro, depSel, metodo, q]);

  // Depósitos disponibles (para chips de admin/ventas)
  const depositos = useMemo(() => {
    if (depFiltro) return [];
    const set = new Set();
    [...entregados, ...cargados].forEach(p => { if (p.deposito) set.add(p.deposito); });
    return [...set].sort();
  }, [entregados, cargados, depFiltro]);

  // Agrupar por día
  const porDia = useMemo(() => {
    const grupos = [];
    let actual = null;
    for (const p of filas) {
      const key = p._fechaMs ? new Date(p._fechaMs).toISOString().slice(0, 10) : "sin-fecha";
      if (!actual || actual.key !== key) {
        actual = { key, fechaMs: p._fechaMs, items: [] };
        grupos.push(actual);
      }
      actual.items.push(p);
    }
    return grupos;
  }, [filas]);

  return (
    <div className="container" style={{ paddingBottom: "40px", maxWidth: "760px" }}>

      {/* Header */}
      <header className="topbar card" style={{ marginBottom: "16px" }}>
        <button onClick={() => navigate(-1)} className="btn btn--ghost btn-sm">⬅ Volver</button>
        <div>
          <h1 className="h1" style={{ margin: 0, fontSize: "17px" }}>📋 Historial de cargas y entregas</h1>
          <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
            {filas.length} registro{filas.length !== 1 ? "s" : ""}{depFiltro ? ` · ${depFiltro}` : ""}
          </p>
        </div>
        <div />
      </header>

      {/* Buscador */}
      <input
        className="input"
        placeholder="🔍 Buscar cliente, #pedido, destino…"
        value={q}
        onChange={e => setQ(e.target.value)}
        style={{ width: "100%", marginBottom: "10px" }}
      />

      {/* Chips de método */}
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: depositos.length > 1 ? "8px" : "14px" }}>
        {METODOS_FILTRO.map(m => {
          const active = metodo === m.id;
          return (
            <button key={m.id || "todos"} onClick={() => setMetodo(m.id)}
              style={{ padding: "5px 12px", borderRadius: "999px", border: "none", cursor: "pointer",
                background: active ? "#0f172a" : "#f1f5f9", color: active ? "#fff" : "#64748b",
                fontSize: "0.78rem", fontWeight: active ? 700 : 500 }}>
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Chips de depósito (solo admin/ventas con más de uno) */}
      {depositos.length > 1 && (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
          {["", ...depositos].map(d => {
            const active = depSel === d;
            return (
              <button key={d || "todos-dep"} onClick={() => setDepSel(d)}
                style={{ padding: "4px 11px", borderRadius: "999px", border: "1px solid #e2e8f0", cursor: "pointer",
                  background: active ? "#1d4ed8" : "#fff", color: active ? "#fff" : "#64748b",
                  fontSize: "0.74rem", fontWeight: active ? 700 : 500 }}>
                🏭 {d || "Todos"}
              </button>
            );
          })}
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>Cargando…</div>
      ) : filas.length === 0 ? (
        <div style={{ textAlign: "center", padding: "64px 20px", color: "#94a3b8" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>📭</div>
          <strong style={{ color: "#334155", display: "block", fontSize: "16px" }}>Sin registros</strong>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {porDia.map(g => (
            <div key={g.key}>
              <p style={{ margin: "0 0 8px", fontSize: "0.72rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", textTransform: "capitalize" }}>
                {fmtDiaLargo(g.fechaMs)} · {g.items.length}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {g.items.map(p => {
                  const ms = METODO_STYLE[p.metodoEntrega] || { bg: "#f1f5f9", color: "#475569", label: p.metodoEntrega || "—" };
                  const entregado = p._tipo === "ENTREGADO";
                  const cam = p.camionId ? camiones[p.camionId] : null;
                  const esViaje = p.metodoEntrega === "CAMION" || p.metodoEntrega === "GIRA";
                  return (
                    <div key={p.id} className="card" style={{ padding: "12px 14px", borderLeft: `4px solid ${ms.color}`, borderRadius: "0 12px 12px 0" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a" }}>#{p.numero || p.id}</span>
                            <span style={{ fontSize: "0.66rem", fontWeight: 700, color: ms.color, background: ms.bg, padding: "1px 8px", borderRadius: "999px" }}>{ms.label}</span>
                            <span style={{ fontSize: "0.66rem", fontWeight: 700, color: entregado ? "#15803d" : "#0369a1", background: entregado ? "#dcfce7" : "#e0f2fe", padding: "1px 8px", borderRadius: "999px" }}>
                              {entregado ? "✓ Entregado" : "🚛 Cargado"}
                            </span>
                            {!depFiltro && p.deposito && (
                              <span style={{ fontSize: "0.64rem", color: "#94a3b8" }}>🏭 {p.deposito}</span>
                            )}
                          </div>
                          <p style={{ margin: "3px 0 0", fontSize: "0.85rem", color: "#334155", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {p.cliente || "—"}
                          </p>

                          {/* Datos del viaje (camión/gira) */}
                          {esViaje && cam && (
                            <div style={{ marginTop: "5px", fontSize: "0.74rem", color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "6px 9px" }}>
                              <strong style={{ color: "#0f172a" }}>{cam.tipo === "CAMION" ? "🚛" : "🚚"} {cam.descripcion || cam.destino || "Viaje"}</strong>
                              {cam.fechaSalida && <span> · salida {fmtFechaCorta(tsToMs(cam.fechaSalida))}</span>}
                              {cam.estado === "CERRADO" && <span style={{ color: "#16a34a", fontWeight: 700 }}> · cerrado</span>}
                            </div>
                          )}
                          {/* Agencia */}
                          {p.metodoEntrega === "AGENCIA" && p.agencia && (
                            <div style={{ marginTop: "5px", fontSize: "0.74rem", color: "#475569" }}>
                              📍 Agencia: <strong>{p.agencia}</strong>
                            </div>
                          )}
                        </div>

                        {/* Hora + bultos */}
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          {p._fechaMs > 0 && <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{fmtHora(p._fechaMs)}</div>}
                          {p.bultos != null && <div style={{ fontSize: "0.68rem", color: "#64748b", marginTop: "2px" }}>📫 {p.bultos} bulto{Number(p.bultos) !== 1 ? "s" : ""}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
